/**
 * CSV line parsing — the ONE isomorphic quote-aware field splitter shared by the
 * frontend custom-importer preview (`import/utils/csv-parser.ts`) and the backend
 * file processor (`imports/.../file-processor.service.ts`).
 *
 * Before consolidation each side hand-rolled the same quote-handling loop, and
 * they had drifted: the frontend trimmed every field, the backend did not — so a
 * space-padded header (BitMart ` type `) previewed as `type` but a saved-template
 * JOB import kept ` type ` and failed to resolve the column. One function, one
 * behavior; the only intentional difference is exposed as the `trimFields`
 * option (frontend trims, backend preserves) so neither consumer changes.
 *
 * Scope: this splits ONE already-delimited physical line. It deliberately does
 * NOT own: (a) splitting content into logical lines across newlines-inside-quotes
 * (the frontend's `splitCSVIntoLogicalLines`), nor (b) the backend's `WHITESPACE`
 * fixed-width mode — those are consumer-specific and stay where they live.
 */

export interface ParseDelimitedCsvLineOptions {
  /**
   * Trim leading/trailing whitespace from every field. The frontend preview
   * sets this `true` (and is the source of truth the user maps against); the
   * backend leaves it `false` to preserve raw values. Default `false`.
   */
  trimFields?: boolean
}

/**
 * Split a single delimited CSV line into fields, honoring double-quoted fields,
 * escaped quotes (`""` inside a quoted field → a literal `"`), and the delimiter
 * appearing inside quotes.
 */
export function parseDelimitedCsvLine(
  line: string,
  delimiter: string,
  options: ParseDelimitedCsvLineOptions = {},
): string[] {
  const trimFields = options.trimFields ?? false
  const result: string[] = []
  let current = ''
  let inQuotes = false
  let i = 0

  while (i < line.length) {
    const char = line[i]

    if (inQuotes) {
      if (char === '"') {
        // An escaped quote ("") inside a quoted field → one literal quote.
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i += 2
        } else {
          // Closing quote of the field.
          inQuotes = false
          i++
        }
      } else {
        current += char
        i++
      }
    } else if (char === '"') {
      // Opening quote of a field.
      inQuotes = true
      i++
    } else if (char === delimiter) {
      result.push(trimFields ? current.trim() : current)
      current = ''
      i++
    } else {
      current += char
      i++
    }
  }

  result.push(trimFields ? current.trim() : current)
  return result
}
